// Component: DijeloviList
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faTrash, faFilePdf, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { API_CONFIG, buildApiUrl } from '../config/api';

function DijeloviList() {
  const [dijelovi, setDijelovi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // Added status filter state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(API_CONFIG.ENDPOINTS.DIJELOVI, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Neuspješno dohvaćanje dijelova.');
        const data = await res.json();
        setDijelovi(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleDelete = async (id) => {
    const confirmed = window.confirm('Da li ste sigurni da želite obrisati dio?');
    if (!confirmed) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_CONFIG.ENDPOINTS.DIJELOVI}/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok && res.status !== 204) throw new Error('Brisanje nije uspjelo.');
      setDijelovi((prev) => prev.filter((x) => x.id_dijelovi !== id));
    } catch (err) {
      alert(err.message);
    }
  };

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let result = dijelovi;
    
    // Apply search filter
    if (q) {
      result = result.filter((d) => {
        const naziv = String(d.naziv_dijela || '').toLowerCase();
        const kbroj = String(d.kataloski_broj || '').toLowerCase();
        return naziv.includes(q) || kbroj.includes(q);
      });
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter((d) => {
        const dostupno = Number(d.dostupna_kolicina);
        const min = Number(d.minimalna_kolicina ?? 0);
        let status;
        
        if (dostupno <= min) {
          status = dostupno === 0 ? 'Nema na stanju' : 'Niska zaliha';
        } else {
          status = 'OK';
        }
        
        return status === statusFilter;
      });
    }
    
    return result;
  }, [dijelovi, searchQuery, statusFilter]);

  // Determine role before any early returns to keep hooks order consistent
  const currentUser = useMemo(() => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; } }, []);

  // Dodato: preuzimanje PDF/XLSX
  const handleDownload = async (format) => {
    try {
      const token = localStorage.getItem('token');
      // Include both search query and status filter in export requests
      const params = {
        format,
        ...(searchQuery.trim() && { q: searchQuery.trim() }),
        ...(statusFilter !== 'all' && { status: statusFilter })
      };
      const url = buildApiUrl(API_CONFIG.ENDPOINTS.DIJELOVI, params);
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error('Neuspješno preuzimanje.');
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = format === 'pdf' ? 'lista-dijelova.pdf' : 'lista-dijelova.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      alert(e.message);
    }
  };

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const page = Math.min(currentPage, totalPages);
  const startIndex = (page - 1) * itemsPerPage;
  const visible = filtered.slice(startIndex, startIndex + itemsPerPage);

  if (loading) return <div className="flex items-center justify-center min-h-[50vh] text-gray-600">Učitavanje...</div>;
  if (error) return (
    <div className="max-w-3xl mx-auto p-4">
      <div className="p-3 mb-4 text-sm text-red-700 bg-red-100 rounded-lg">Greška: {error}</div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-2xl font-semibold text-gray-900">Lista dijelova</h2>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="w-full sm:w-80">
            <input
              type="text"
              placeholder="Pretraga (naziv, kataloški broj)"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>
          <div className="w-full sm:w-48">
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
            >
              <option value="all">Svi statusi</option>
              <option value="OK">OK</option>
              <option value="Niska zaliha">Niska zaliha</option>
              <option value="Nema na stanju">Nema na stanju</option>
            </select>
          </div>
        </div>
        {(currentUser?.role === 'admin' || currentUser?.role === 'user') && (
          <Link to="/new-dio" className="inline-flex">
            <button className="px-4 py-2 rounded-md bg-purple-600 text-white hover:bg-purple-700 transition-colors">Dodaj novi dio</button>
          </Link>
        )}
        <div className="inline-flex items-center gap-2">
          <button onClick={() => handleDownload('pdf')} className="px-3 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 transition-colors inline-flex items-center gap-2" title="Preuzmi PDF">
            <FontAwesomeIcon icon={faFilePdf} />
            PDF
          </button>
          <button onClick={() => handleDownload('xlsx')} className="px-3 py-2 rounded-md bg-green-600 text-white hover:bg-green-700 transition-colors inline-flex items-center gap-2" title="Preuzmi Excel">
            <FontAwesomeIcon icon={faFileExcel} />
            Excel
          </button>
          {currentUser?.role === 'user' && (
            <Link to="/reports/consumed-parts" className="inline-flex">
              <button className="px-3 py-2 rounded-md bg-sky-600 text-white hover:bg-sky-700 transition-colors">
                Utrošeni dijelovi – izvještaj
              </button>
            </Link>
          )}
        </div>
      </div>
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Naziv dijela</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kataloški broj</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dostupna količina</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Minimalna količina</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Akcije</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-gray-500">Nema zapisa.</td>
                </tr>
              ) : (
                visible.map((d) => (
                  <tr key={d.id_dijelovi} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{d.naziv_dijela}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{d.kataloski_broj}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{d.dostupna_kolicina}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{d.minimalna_kolicina ?? 0}</td>
                    <td className="px-4 py-3 text-sm">
                      {Number(d.dostupna_kolicina) <= Number(d.minimalna_kolicina ?? 0) ? (
                        <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${Number(d.dostupna_kolicina) === 0 ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>
                          {Number(d.dostupna_kolicina) === 0 ? 'Nema na stanju' : 'Niska zaliha'}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800">OK</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-right">
                      <div className="inline-flex gap-2">
                        {(currentUser?.role === 'admin' || currentUser?.role === 'user') && (
                          <Link to={`/dijelovi/${d.id_dijelovi}/edit`}>
                            <button className="px-3 py-2 rounded-md bg-amber-600 text-white hover:bg-amber-700 transition-colors" title="Uredi">
                              <FontAwesomeIcon icon={faPenToSquare} />
                            </button>
                          </Link>
                        )}
                        {(currentUser?.role === 'admin' || currentUser?.role === 'user') && (
                          <button onClick={() => handleDelete(d.id_dijelovi)} className="px-3 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors" title="Obriši">
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="px-1 sm:px-0 flex items-center justify-between text-sm text-gray-700">
        <div>Stranica {page} od {totalPages}</div>
        <div className="flex items-center gap-2">
          <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={page === 1} className={`px-3 py-1 rounded-md border ${page === 1 ? 'text-gray-400 border-gray-200' : 'hover:bg-gray-50 border-gray-300'}`}>Prethodna</button>
          <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className={`px-3 py-1 rounded-md border ${page === totalPages ? 'text-gray-400 border-gray-200' : 'hover:bg-gray-50 border-gray-300'}`}>Sljedeća</button>
        </div>
      </div>
    </div>
  );
};

export default DijeloviList;
